import React, { useCallback, useEffect, useState } from 'react'
import {
  Handshake,
  Plus,
  Pencil,
  Trash2,
  Loader2,
  AlertCircle,
  CheckCircle2,
  Play,
  RefreshCw,
} from 'lucide-react'
import { collaborationProgramsAPI, platformAPI } from '../services/api'

/**
 * Super-admin catalog of collaboration programs (Titanium & friends), plus the
 * switch that lets the nightly job actually close expired partner tenants.
 *
 * The switch is a platform setting rather than a constant on purpose: the job
 * ships default-off so its output can be read in production for a cycle before
 * it is allowed to lock anyone out.
 */

const AUTO_DEACTIVATE_KEY = 'collaboration_programs.auto_deactivate_enabled'

const EMPTY_PROGRAM = {
  name: '',
  key: '',
  partner_name: '',
  description: '',
  default_duration_months: 12,
  is_active: true,
}

function apiErrorMessage(err, fallback = 'Algo deu errado. Tente novamente.') {
  const data = err?.response?.data
  if (typeof data?.error === 'string') return data.error
  if (data?.error && typeof data.error === 'object') return data.error.message || data.error.code || fallback
  return data?.message || err?.message || fallback
}

const ProgramFormModal = ({ program, isNew, onSave, onClose }) => {
  const [form, setForm] = useState(program || EMPTY_PROGRAM)
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState('')

  const set = (field, value) => setForm((f) => ({ ...f, [field]: value }))

  const handleSubmit = async () => {
    setError('')
    if (!form.name.trim()) return setError('Informe o nome.')
    if (Number(form.default_duration_months) < 1) {
      return setError('A duração padrão deve ser de pelo menos 1 mês.')
    }
    setSaving(true)
    try {
      await onSave({
        ...form,
        name: form.name.trim(),
        key: (form.key || '').trim().toLowerCase() || undefined,
        default_duration_months: Number(form.default_duration_months),
      })
      onClose()
    } catch (err) {
      setError(apiErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
      <div className="flex min-h-full items-center justify-center p-4">
        <div className="fixed inset-0 bg-gray-600 bg-opacity-75" onClick={onClose} />
        <div className="relative w-full max-w-lg rounded-xl bg-white shadow-2xl">
          <div className="border-b border-admin-200 p-6">
            <h2 className="text-xl font-semibold text-admin-900">
              {isNew ? 'Novo programa de colaboração' : `Editar ${program.name}`}
            </h2>
          </div>

          <div className="space-y-4 p-6">
            {error && (
              <div className="flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
                <AlertCircle className="h-4 w-4 flex-shrink-0" />
                {error}
              </div>
            )}

            <div>
              <label className="mb-1 block text-sm font-medium text-admin-700">Nome *</label>
              <input
                className="input"
                value={form.name}
                onChange={(e) => set('name', e.target.value)}
                placeholder="Titanium"
                disabled={saving}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-admin-700">Parceiro</label>
              <input
                className="input"
                value={form.partner_name || ''}
                onChange={(e) => set('partner_name', e.target.value)}
                placeholder="Escola do Vestir"
                disabled={saving}
              />
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-admin-700">Chave</label>
              <input
                className="input font-mono"
                value={form.key || ''}
                onChange={(e) => set('key', e.target.value)}
                placeholder="titanium"
                disabled={saving || !isNew}
              />
              <p className="mt-1 text-xs text-admin-500">
                {isNew
                  ? 'Deixe em branco para gerá-la a partir do nome.'
                  : 'Fixa após a criação — o histórico de vínculos aponta para ela.'}
              </p>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-admin-700">
                Duração padrão (meses)
              </label>
              <input
                type="number"
                min={1}
                className="input"
                value={form.default_duration_months}
                onChange={(e) => set('default_duration_months', e.target.value)}
                disabled={saving}
              />
              <p className="mt-1 text-xs text-admin-500">
                Pré-seleciona a duração quando um estúdio é adicionado a este programa.
              </p>
            </div>

            <div>
              <label className="mb-1 block text-sm font-medium text-admin-700">Descrição</label>
              <textarea
                rows={3}
                className="input"
                value={form.description || ''}
                onChange={(e) => set('description', e.target.value)}
                disabled={saving}
              />
            </div>

            <label className="flex items-center gap-2 text-sm text-admin-700">
              <input
                type="checkbox"
                checked={form.is_active}
                onChange={(e) => set('is_active', e.target.checked)}
                disabled={saving}
              />
              Disponível ao adicionar um estúdio a um programa
            </label>
          </div>

          <div className="flex items-center justify-end gap-3 border-t border-admin-200 p-6">
            <button onClick={onClose} className="btn btn-secondary" disabled={saving}>
              Cancelar
            </button>
            <button onClick={handleSubmit} className="btn btn-primary" disabled={saving}>
              {saving ? (
                <span className="flex items-center">
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Salvando...
                </span>
              ) : (
                'Salvar'
              )}
            </button>
          </div>
        </div>
      </div>
    </div>
  )
}

export const CollaborationProgramsCard = ({ readOnly = false }) => {
  const [programs, setPrograms] = useState([])
  const [autoDeactivate, setAutoDeactivate] = useState(false)
  const [loading, setLoading] = useState(true)
  const [savingSetting, setSavingSetting] = useState(false)
  const [running, setRunning] = useState(false)
  const [error, setError] = useState('')
  const [notice, setNotice] = useState('')
  const [modal, setModal] = useState(null) // {program, isNew}
  const [deleteTarget, setDeleteTarget] = useState(null)

  const load = useCallback(async () => {
    setLoading(true)
    setError('')
    try {
      const [programsRes, settingsRes] = await Promise.all([
        collaborationProgramsAPI.getAll({ include_inactive: 1 }),
        platformAPI.getPlatformSettings(),
      ])
      setPrograms(programsRes.data?.data || [])
      setAutoDeactivate(Boolean((settingsRes.data?.data || {})[AUTO_DEACTIVATE_KEY]))
    } catch (err) {
      setError(apiErrorMessage(err, 'Falha ao carregar os programas de colaboração'))
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => {
    load()
  }, [load])

  const handleSave = async (form) => {
    if (modal?.isNew) await collaborationProgramsAPI.create(form)
    else await collaborationProgramsAPI.update(modal.program.id, form)
    await load()
  }

  const handleDelete = async () => {
    setError('')
    try {
      await collaborationProgramsAPI.remove(deleteTarget.id)
      setDeleteTarget(null)
      await load()
    } catch (err) {
      setError(apiErrorMessage(err, 'Falha ao desativar o programa'))
      setDeleteTarget(null)
    }
  }

  const handleToggleAutoDeactivate = async () => {
    setSavingSetting(true)
    setError('')
    setNotice('')
    try {
      await platformAPI.updatePlatformSettings({ [AUTO_DEACTIVATE_KEY]: !autoDeactivate })
      setAutoDeactivate((v) => !v)
      setNotice('Salvo.')
    } catch (err) {
      setError(apiErrorMessage(err, 'Falha ao salvar a configuração'))
    } finally {
      setSavingSetting(false)
    }
  }

  const handleDryRun = async () => {
    setRunning(true)
    setError('')
    setNotice('')
    try {
      const res = await collaborationProgramsAPI.runExpiry(true)
      const s = res.data?.data || {}
      const wouldClose = (s.tenants || []).filter((t) => t.would_deactivate).length
      setNotice(
        `Simulação: ${s.checked} vínculo(s) após a data de término, ${wouldClose} estúdio(s) perderiam o acesso. Nada foi alterado.`
      )
    } catch (err) {
      setError(apiErrorMessage(err, 'Falha na simulação'))
    } finally {
      setRunning(false)
    }
  }

  return (
    <div className="card p-6 mb-6">
      <div className="mb-1 flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Handshake className="h-5 w-5 text-primary-600" />
          <h2 className="text-lg font-semibold text-admin-900">Programas de colaboração</h2>
        </div>
        <div className="flex items-center gap-2">
          <button
            className="rounded p-1.5 text-admin-600 hover:bg-admin-100"
            onClick={load}
            title="Atualizar"
          >
            <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} />
          </button>
          {!readOnly && (
          <button onClick={() => setModal({ program: null, isNew: true })} className="btn btn-primary">
            <Plus className="mr-2 h-4 w-4" />
            Novo programa
          </button>
          )}
        </div>
      </div>
      <p className="mb-6 text-sm text-admin-600">
        Parcerias às quais um estúdio pode ser vinculado pela página de detalhes. Um vínculo libera
        o acesso por um período fixo, independentemente de pagamento, e protege o estúdio de suspensão
        por falta de pagamento até o fim do período.
      </p>

      {error && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-800">
          <AlertCircle className="h-4 w-4 flex-shrink-0" />
          {error}
        </div>
      )}
      {notice && (
        <div className="mb-4 flex items-center gap-2 rounded-lg border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800">
          <CheckCircle2 className="h-4 w-4 flex-shrink-0" />
          {notice}
        </div>
      )}

      {loading ? (
        <div className="flex items-center gap-2 text-admin-500">
          <Loader2 className="h-4 w-4 animate-spin" />
          Carregando…
        </div>
      ) : programs.length === 0 ? (
        <p className="text-sm text-admin-500">Nenhum programa configurado ainda.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[640px] text-left text-sm">
            <thead>
              <tr className="text-admin-500">
                <th className="pb-2 pr-3 font-medium whitespace-nowrap">Programa</th>
                <th className="pb-2 pr-3 font-medium whitespace-nowrap">Parceiro</th>
                <th className="pb-2 pr-3 font-medium whitespace-nowrap">Chave</th>
                <th className="pb-2 pr-3 font-medium whitespace-nowrap">Prazo padrão</th>
                <th className="pb-2 pr-3 font-medium whitespace-nowrap">Status</th>
                <th className="pb-2 font-medium whitespace-nowrap text-right">Ações</th>
              </tr>
            </thead>
            <tbody>
              {programs.map((program) => (
                <tr key={program.id} className="border-b border-admin-100">
                  <td className="py-2.5 pr-3 font-medium text-admin-900">{program.name}</td>
                  <td className="py-2.5 pr-3 text-admin-700">{program.partner_name || '—'}</td>
                  <td className="py-2.5 pr-3 font-mono text-xs text-admin-600">{program.key}</td>
                  <td className="py-2.5 pr-3 text-admin-700">
                    {program.default_duration_months} {Number(program.default_duration_months) === 1 ? 'mês' : 'meses'}
                  </td>
                  <td className="py-2.5 pr-3">
                    <span
                      className={`inline-flex items-center rounded px-2 py-0.5 text-xs font-medium ${
                        program.is_active
                          ? 'bg-green-100 text-green-700'
                          : 'bg-admin-100 text-admin-700'
                      }`}
                    >
                      {program.is_active ? 'Ativo' : 'Inativo'}
                    </span>
                  </td>
                  <td className="py-2.5 text-right">
                    {!readOnly && (
                    <button
                      onClick={() => setModal({ program, isNew: false })}
                      className="rounded-lg p-2 text-admin-600 hover:bg-admin-100 hover:text-admin-900"
                      title="Editar"
                    >
                      <Pencil className="h-4 w-4" />
                    </button>
                    )}
                    {!readOnly && program.is_active && (
                      <button
                        onClick={() => setDeleteTarget(program)}
                        className="rounded-lg p-2 text-admin-600 hover:bg-red-50 hover:text-red-600"
                        title="Desativar"
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {!readOnly && (
      <div className="mt-6 border-t border-admin-100 pt-5">
        <h3 className="mb-3 text-sm font-semibold uppercase tracking-wider text-admin-500">
          Automação de expiração
        </h3>
        <label className="flex items-start gap-3">
          <input
            type="checkbox"
            className="mt-1"
            checked={autoDeactivate}
            onChange={handleToggleAutoDeactivate}
            disabled={savingSetting}
          />
          <span className="text-sm text-admin-700">
            <strong className="text-admin-900">
              Desativar estúdios cujo programa terminou sem renovação da assinatura
            </strong>
            <br />
            A rotina noturna (01:30, horário de São Paulo) sempre marca os vínculos encerrados como expirados. Com esta
            opção desligada, ela para aí e apenas registra o que teria feito — ative depois de analisar
            algumas execuções. Os estúdios desativados recebem o motivo “End of partnership program. Subscription
            not renewed.” (fim do programa de parceria, assinatura não renovada).
          </span>
        </label>

        <button
          onClick={handleDryRun}
          className="btn btn-secondary mt-4"
          disabled={running}
        >
          {running ? (
            <span className="flex items-center">
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Executando…
            </span>
          ) : (
            <span className="flex items-center">
              <Play className="mr-2 h-4 w-4" />
              Pré-visualizar a rotina desta noite
            </span>
          )}
        </button>
      </div>
      )}

      {modal && (
        <ProgramFormModal
          program={modal.program}
          isNew={modal.isNew}
          onSave={handleSave}
          onClose={() => setModal(null)}
        />
      )}

      {deleteTarget && (
        <div className="fixed inset-0 z-50 overflow-y-auto" role="dialog" aria-modal="true">
          <div className="flex min-h-full items-center justify-center p-4">
            <div
              className="fixed inset-0 bg-gray-600 bg-opacity-75"
              onClick={() => setDeleteTarget(null)}
            />
            <div className="relative w-full max-w-md rounded-xl bg-white p-6 shadow-2xl">
              <h3 className="mb-2 text-lg font-semibold text-admin-900">
                Desativar “{deleteTarget.name}”?
              </h3>
              <p className="mb-4 text-sm text-admin-600">
                Ele deixa de ser oferecido ao adicionar um estúdio a um programa. Os vínculos anteriores continuam
                referenciando-o. Os estúdios vinculados atualmente precisam ser removidos antes.
              </p>
              <div className="flex justify-end gap-3">
                <button onClick={() => setDeleteTarget(null)} className="btn btn-secondary">
                  Cancelar
                </button>
                <button
                  onClick={handleDelete}
                  className="btn btn-primary bg-red-600 hover:bg-red-700"
                >
                  Desativar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  )
}

export default CollaborationProgramsCard
